import "./EditMainBookerPopup.scss";
import MidPopup from "../../../hoc/MidPopup/MidPopup";
import {
  IBooking,
  IChangeMainBookerInput,
  ICourt,
  ISummaryItem,
  IUser,
} from "../../../types/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { changeMainBooker } from "../../../api/bookings/bookings";
import { toast } from "react-toastify";
import { capitalizeWords } from "../../../shared/utility";
import { searchUsers } from "../../../api/users/users";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";

interface IEditMainBookerPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  booking: IBooking;
}

function EditMainBookerPopup(props: IEditMainBookerPopupProps) {
  const { onClose, onConfirm, showPopup, booking } = props;
  const [isChanging, setIsChanging] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const handleClose = () => {
    onClose();
  };

  const lastWarningTimeRef = useRef<number>(0);

  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestUserSearchRef = useRef<string>("");

  const fetchSearchUsers = async (search: string) => {
    latestUserSearchRef.current = search;
    let requestor = "admin" as "admin" | "user";
    try {
      const response = await searchUsers(search, requestor, true, "CO_PLAYER");
      if (latestUserSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }
      setSearchedUsers(response);
    } catch (error: any) {
      toast.error(error);
    }
  };
  useEffect(() => {
    if (searchString.length > 0) {
      handleSearchUsers();
    } else {
      setSearchedUsers([]);
    }
  }, [searchString]);

  const handleSearchUsers = () => {
    fetchSearchUsers(searchString);
  };
  /** End search users  */

  
  const handleChoosePlayer = (player: IUser) => {
    setSelectedUser(player);
  };

  const handleChangeMainBooker = async () => {
    if (!selectedUser) {
      return;
    }
    setIsChanging(true);
    const isChangingToast = toast.loading("Ändrar huvudbokare...");

    const changeMainBookerInput: IChangeMainBookerInput = {
      booking_id: booking.booking_id,
      member_id: booking.member_id,
      datetime_start: booking.datetime_start,
      member_name: booking.member_name,
      member_status: booking.member_status,
      status: booking.status,
      slot_index: booking.slot_index,
      co_players: booking.co_players,
      coach_id: booking.coach_id,
      court_id: booking.court_id,
    };
    try {
      const response = await changeMainBooker(
        changeMainBookerInput,
        selectedUser.member_id
      );
      if (response && !("message" in response)) {
        setIsChanging(false);
        toast.dismiss(isChangingToast);
        toast.success("Huvudbokaren har ändrats");
        onConfirm();
        onClose();
      } else {
        toast.dismiss(isChangingToast);
        setIsChanging(false);
      }
    } catch (error: any) {
      toast.dismiss(isChangingToast);
      setIsChanging(false);
      toast.error(error);
    }
  };

  return (
    <MidPopup
      showPopup={showPopup}
      onClose={handleClose}
      onConfirm={onConfirm as any}
      leftTopElement={<span className="text-m">Välj ny huvudbokare</span>}
      isLoading={isChanging}
    >
      <div className="choose-users-popup-wrapper">
        <div className="user-input-btn-wrapper">
          <div className="search-input-wrapper">
            <input
              className="users-search-input text-m"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Sök på namn & medlemsnummer"
              ref={inputRef}
            ></input>
            <i className="fa-regular fa-magnifying-glass search-icon"></i>
          </div>
        </div>
        {searchedUsers.length > 0 &&
          searchedUsers.map((player, index) => {
            if (index > 4) {
              return null;
            }
            const isFrozen = player.frozen;
            let frozenClass = "";
            if (isFrozen) {
              frozenClass = "frozen";
            }

            if (props.booking.member_id === player.member_id) {
              return null;
            }
            return (
              <div
                key={player.member_id}
                className="player-wrapper pointer text-m"
                onClick={() => handleChoosePlayer(player)}
              >
                <div className={`player-name ${frozenClass}`}>
                  <span>
                    {capitalizeWords(player.first_name)}{" "}
                    {capitalizeWords(player.last_name)} ({player.member_type}){" "}
                    {isFrozen && " - medlem fryst"}
                  </span>
                </div>
                <div className="player-id">{player.member_id}</div>
                <a className="choose-player">Välj medlem</a>
              </div>
            );
          })}
      </div>

      <HandleConfirmationPopup
        showPopup={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        onConfirm={() => handleChangeMainBooker()}
        questionText={`Är du säker på att du vill ändra huvudbokaren till ${capitalizeWords(
          selectedUser?.first_name || ""
        )} ${capitalizeWords(selectedUser?.last_name || "")}?`}
        confirmText="Ja"
        denyText="Nej"
      />
    </MidPopup>
  );
}

export default EditMainBookerPopup;
